import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import { useIncidents, useCategories } from '../hooks/useIncidents';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { IncidentStatusBadge } from '../components/IncidentStatusBadge';
import { DEGREE_LABELS, type IncidentStatus, type IncidentDegree } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Função helper para variant de status (não utilizada atualmente - usando IncidentStatusBadge)
// const getStatusVariant = (status: IncidentStatus): 'success' | 'default' | 'warning' | 'error' => {
//   switch (status) {
//     case 'APPROVED':
//     case 'CLOSED':
//       return 'success';
//     case 'MONITORING':
//       return 'warning';
//     case 'PENDING_APPROVAL':
//       return 'default';
//     case 'CANCELLED':
//       return 'error';
//     default:
//       return 'default';
//   }
// };

const getDegreeVariant = (degree: IncidentDegree): 'success' | 'default' | 'warning' | 'error' => {
  switch (degree) {
    case 'D0':
    case 'D1':
      return 'default';
    case 'D2':
      return 'warning';
    case 'D3':
    case 'D4':
      return 'error';
    case 'D5':
      return 'error';
    default:
      return 'default';
  }
};

export function IncidentsListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [page, setPage] = useState(1);

  const { data: categoriesData = [] } = useCategories();

  // Query principal com filtros
  const { data: incidentsData, isLoading } = useIncidents({
    page,
    limit: 20,
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter,
  });

  // Queries para contagem de cada status (sem paginação)
  const { data: allIncidentsData } = useIncidents({
    limit: 1,
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter
  });
  const { data: pendingData } = useIncidents({
    limit: 1,
    status: 'PENDING_APPROVAL',
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter
  });
  const { data: approvedData } = useIncidents({
    limit: 1,
    status: 'APPROVED',
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter
  });
  const { data: monitoringData } = useIncidents({
    limit: 1,
    status: 'MONITORING',
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter
  });
  const { data: closedData } = useIncidents({
    limit: 1,
    status: 'CLOSED',
    categoryId: categoryFilter === 'ALL' ? undefined : categoryFilter
  });

  const incidents = incidentsData?.data ?? [];
  const pagination = incidentsData?.pagination;

  const filteredIncidents = useMemo(() => {
    if (!searchQuery) return incidents;

    const query = searchQuery.toLowerCase();
    return incidents.filter(
      (incident) =>
        incident.employee?.user.name.toLowerCase().includes(query) ||
        incident.employee?.user.email.toLowerCase().includes(query) ||
        incident.description.toLowerCase().includes(query) ||
        incident.category?.name.toLowerCase().includes(query)
    );
  }, [incidents, searchQuery]);

  // Calcular contadores por status usando as queries de contagem
  const statusCounts = useMemo(() => {
    return {
      ALL: allIncidentsData?.pagination?.total ?? 0,
      PENDING_APPROVAL: pendingData?.pagination?.total ?? 0,
      APPROVED: approvedData?.pagination?.total ?? 0,
      MONITORING: monitoringData?.pagination?.total ?? 0,
      CLOSED: closedData?.pagination?.total ?? 0,
    };
  }, [allIncidentsData, pendingData, approvedData, monitoringData, closedData]);

  const statusTabs: Array<{ label: string; value: IncidentStatus | 'ALL' }> = [
    { label: `Todos (${statusCounts.ALL})`, value: 'ALL' },
    { label: `Pendentes (${statusCounts.PENDING_APPROVAL})`, value: 'PENDING_APPROVAL' },
    { label: `Aprovados (${statusCounts.APPROVED})`, value: 'APPROVED' },
    { label: `Monitoramento (${statusCounts.MONITORING})`, value: 'MONITORING' },
    { label: `Fechados (${statusCounts.CLOSED})`, value: 'CLOSED' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Central de Problemas</h1>
          <p className="text-muted-foreground">Gestão de ocorrências e política disciplinar</p>
        </div>
        <Link to="/incident-management/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Registrar Incidente
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar por colaborador, email ou descrição..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                options={[
                  { value: 'ALL', label: 'Todas as categorias' },
                  ...categoriesData.map((category) => ({
                    value: category.id,
                    label: category.name,
                  })),
                ]}
              />
            </div>

            <TabFilter
              tabs={statusTabs}
              currentTab={statusFilter}
              onChange={(value) => setStatusFilter(value as IncidentStatus | 'ALL')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Incidents List */}
      <div className="grid gap-4">
        {filteredIncidents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhum incidente encontrado
            </CardContent>
          </Card>
        ) : (
          filteredIncidents.map((incident) => (
            <Link key={incident.id} to={`/incident-management/${incident.id}`}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CardTitle className="text-lg">
                          {incident.employee?.user.name}
                        </CardTitle>
                        <Badge variant={getDegreeVariant(incident.appliedDegree)}>
                          {DEGREE_LABELS[incident.appliedDegree]}
                        </Badge>
                        <IncidentStatusBadge incident={incident} />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {incident.category?.name} • Registrado em{' '}
                        {format(new Date(incident.registeredAt), "dd 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })}
                      </p>
                      <p className="text-foreground line-clamp-2">{incident.description}</p>
                    </div>
                    {incident.financialLoss && (
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Prejuízo</p>
                        <p className="text-lg font-bold text-red-600">
                          R$ {Number(incident.financialLoss).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <span className="px-4 py-2">
            Página {page} de {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
